/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useMutation } from '@apollo/client';
import { Button, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, useToast } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import FileFormField from '../../../components/form/FileFormField';
import TextFormField from '../../../components/form/TextFormField';
import c from '../../../constant';

const M_TERMINATE = gql`
	mutation InstantlyTerminatePartner(
		$companyId: String!
		$intermediaryId: String!
		$reason: String!
		$internalReason: String!
		$inactiveDocument: Upload
		$inactiveDocumentSize: Float
	) {
		instantlyTerminatePartner(
			companyId: $companyId
			intermediaryId: $intermediaryId
			reason: $reason
			internalReason: $internalReason
			inactiveDocument: $inactiveDocument
			inactiveDocumentSize: $inactiveDocumentSize
		)
	}
`;

const validationSchema = Yup.object().shape({
	isAgentPartner: Yup.boolean(),
	reason: Yup.mixed().when('isAgentPartner', {
		is: (isAgentPartner) => isAgentPartner === true,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	internalReason: Yup.mixed().when('isAgentPartner', {
		is: (isAgentPartner) => isAgentPartner === true,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	attachment: Yup.mixed()
		.required('required')
		.test('fileSize', 'over limit', (value) => {
			if (value) {
				return value?.size <= c.maxFileSize / 20;
			} else {
				return true;
			}
		}),
});

export default function DeleteCompanyPartnerModal({
	companyId = null,
	intermediaryId = null,
	isAgentPartner = false,
	isOpen = false,
	onClose = () => null,
	onDataChange = () => null,
}) {
	const toast = useToast();
	const { t } = useTranslation();
	const [terminatePartner, { loading }] = useMutation(M_TERMINATE);

	return (
		<>
			<Formik
				validationSchema={validationSchema}
				initialValues={{
					isAgentPartner,
					reason: '',
					internalReason: '',
					attachment: null,
				}}
				onSubmit={(values) => {
					terminatePartner({
						variables: {
							companyId,
							intermediaryId,
							reason: values.reason,
							internalReason: values.internalReason,
							inactiveDocument: values.attachment,
							inactiveDocumentSize: values.attachment?.size,
						},
					})
						.then((result) => {
							onClose();
							onDataChange();
							toast({
								title: t('Operation success'),
								status: 'success',
								isClosable: true,
								position: 'top-right',
							});
						})
						.catch((error) => {
							toast({
								title: t(error?.message ? error?.message : 'something went wrong'),
								status: 'error',
								isClosable: true,
								position: 'bottom-right',
							});
						});
				}}
			>
				{({ errors, values, ...props }) => {
					return (
						<Modal
							size="lg"
							borderRadius={c.borderRadius}
							isOpen={isOpen}
							onClose={onClose}
							closeOnEsc={loading ? false : true}
							closeOnOverlayClick={loading ? false : true}
						>
							<ModalOverlay />
							<ModalContent borderRadius={c.borderRadius}>
								<Form>
									<ModalHeader>{t('Delete Partnered Company')}</ModalHeader>
									<ModalCloseButton isDisabled={loading} />
									<ModalBody>
										{isAgentPartner && <TextFormField {...props} placeholder={t('Reason')} label="Reason" name="reason" required={true} />}
										{isAgentPartner && (
											<TextFormField
												{...props}
												placeholder={t('Internal reason')}
												label="Internal reason"
												name="internalReason"
												required={true}
											/>
										)}
										<FileFormField {...props} label="Attachment" name="attachment" required={true} />
									</ModalBody>
									<ModalFooter>
										<Flex justifyContent="flex-end" alignItems="center">
											<Button variant="solid" size="md" colorScheme="red" type="submit" isLoading={loading}>
												{t('Delete')}
											</Button>
										</Flex>
									</ModalFooter>
								</Form>
							</ModalContent>
						</Modal>
					);
				}}
			</Formik>
		</>
	);
}
